import * as React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import * as analytics from "@/services/analytics";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { FormInput } from "@/components/forms/input";
import { FormSelect } from "@/components/ui/select";
import { useGetSnapshotCollectionsQuery, useGetSubcollectionsQuery } from "../../snapshotCollection.api";
import { BusinessReport, BusinessReportPrompt } from "@/modules/snapshots/businessReports.types";
import { Item } from "@/types/fse";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateBusinessReportOutcomeMutation } from "../../businessReports.api";
import { BATCH_API_AUTO_THRESHOLD_CALLS, BATCH_SIZE, CREATE_NEW_COLLECTION_KEY } from "./consts";
import { GenerateSnapshotCollectionFormInputs } from "./types";
import { getLockedExecutionMode, getSnapshotCollectionOptions, validateSnapshotCollection } from "./helpers";

const NO_SUBCOLLECTION_KEY = "no-subcollection";

interface Props {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  businessReport: BusinessReport;
  fsEntries: Item[];
  variables?: Record<string, string>;
  prompts: BusinessReportPrompt[];
}

export const GenerateSnapshotCollectionModal: React.FC<Props> = ({
  prompts,
  variables,
  isOpen,
  setIsOpen,
  fsEntries,
  businessReport,
}) => {
  const [createBusinessReportOutcome] = useCreateBusinessReportOutcomeMutation();
  const navigate = useNavigate();
  const isBatch = fsEntries.length > 1;
  const estimatedLlmCalls = fsEntries.length * prompts.length;
  const [useBatchApi, setUseBatchApi] = React.useState(estimatedLlmCalls >= BATCH_API_AUTO_THRESHOLD_CALLS);

  const methods = useForm<GenerateSnapshotCollectionFormInputs>({
    defaultValues: {
      document_name: fsEntries.length === 1 ? fsEntries[0].name : "",
      name: fsEntries.length === 1 ? fsEntries[0].name : "",
    },
  });

  const { data: snapshotCollections = [] } = useGetSnapshotCollectionsQuery({
    business_report_id: businessReport.id,
  });

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<GenerateSnapshotCollectionFormInputs>();

  const snapshotCollectionsOptions = React.useMemo(
    () => getSnapshotCollectionOptions(snapshotCollections),
    [snapshotCollections],
  );

  const selectedSnapshotCollection = useWatch({
    control: methods.control,
    name: "snapshot_collection_id",
  });

  React.useEffect(() => {
    if (selectedSnapshotCollection !== CREATE_NEW_COLLECTION_KEY) {
      methods.setValue("snapshot_collection_new", "");
    }
    methods.setValue("subcollection_id", NO_SUBCOLLECTION_KEY);
  }, [methods, selectedSnapshotCollection]);

  const shouldShowSubcollectionSelect =
    !!selectedSnapshotCollection && selectedSnapshotCollection !== CREATE_NEW_COLLECTION_KEY;
  const { data: subcollections = [] } = useGetSubcollectionsQuery(
    { snapshot_collection_id: selectedSnapshotCollection },
    { skip: !shouldShowSubcollectionSelect },
  );

  // Once a collection has started running its execution mode is locked server-side
  // (mixing sync and batch cells in one run is not allowed); mirror that in the UI
  // instead of letting the user tick a box that would be rejected.
  const lockedExecutionMode = React.useMemo(
    () => getLockedExecutionMode(snapshotCollections, selectedSnapshotCollection),
    [snapshotCollections, selectedSnapshotCollection],
  );
  const effectiveUseBatchApi = lockedExecutionMode ? lockedExecutionMode === "batch" : useBatchApi;

  const subcollectionOptions = React.useMemo(
    () => [
      { label: "No sub-collection", value: NO_SUBCOLLECTION_KEY },
      ...subcollections.map(subcollection => ({
        label: subcollection.name,
        value: subcollection.id,
      })),
    ],
    [subcollections],
  );

  const onSubmit = async (payload: GenerateSnapshotCollectionFormInputs) => {
    if (!validateSnapshotCollection(selectedSnapshotCollection, payload, methods.setError)) {
      return;
    }

    try {
      let snapshotCollectionId = null;
      let result;

      for (let i = 0; i < fsEntries.length; i += BATCH_SIZE) {
        const batched = fsEntries.slice(i, i + BATCH_SIZE);
        try {
          result = await createBusinessReportOutcome({
            fs_entries: batched.map(entry => entry.id),
            business_report_id: businessReport.id,
            business_report_prompts: prompts.map(prompt => ({
              order: prompt.order,
              full_context: prompt.full_context,
              business_report_prompt_id: prompt.id,
              prompt: prompt.prompt,
              name: prompt.name,
              is_date: prompt.is_date,
              data_type: prompt.data_type ?? (prompt.is_date ? "date" : null),
              use_image: prompt.use_image,
              no_retriever: prompt.no_retriever,
              web_search: prompt.web_search,
              traffic_light: prompt.traffic_light,
            })),
            snapshot_collection_id: snapshotCollectionId
              ? snapshotCollectionId
              : payload.snapshot_collection_id === CREATE_NEW_COLLECTION_KEY
                ? undefined
                : payload.snapshot_collection_id,
            snapshot_collection_new: snapshotCollectionId ? undefined : payload.snapshot_collection_new,
            subcollection_id: payload.subcollection_id !== NO_SUBCOLLECTION_KEY ? payload.subcollection_id : undefined,
            variables: variables || {},
            name: payload.name,
            execution_mode: isBatch ? (effectiveUseBatchApi ? "batch" : "sync") : undefined,
          }).unwrap();
        } catch (error) {
          toast.error("Error while saving");
          Sentry.captureException(error);
          return;
        }

        if (!snapshotCollectionId) {
          snapshotCollectionId = result.snapshot_collection_id;
        }
      }

      setIsOpen(false);
      toast.success(snapshotCollectionId ? "SnapShot collection updated" : "SnapShot created");
      const selectedSubcollectionId =
        payload.subcollection_id !== NO_SUBCOLLECTION_KEY ? payload.subcollection_id : undefined;
      analytics.snapshotCreate(
        businessReport.name,
        fsEntries.map(f => f.name),
      );

      if (snapshotCollectionId) {
        navigate(
          `/snapshots/collections?businessReportId=${businessReport.id}&snapshotCollectionId=${snapshotCollectionId}&refresh=true${selectedSubcollectionId ? `&subcollection_id=${selectedSubcollectionId}` : ""}`,
        );
      } else {
        const outcomeId = result?.business_report_outcome_ids?.[0];
        if (outcomeId) {
          navigate(`/reports/outcome/${outcomeId}`);
        }
      }
    } catch (error) {
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{isBatch ? "Saving Snapshot to Collection" : "Saving Snapshot"}</DialogTitle>
            </DialogHeader>
            {!isBatch && (
              <>
                <FormInput
                  autoFocus={false}
                  name="document_name"
                  readOnly={true}
                  placeholder="Document name"
                  className="my-3"
                />

                <FormInput autoFocus={false} name="name" placeholder="SnapShot name" className="my-3" />
              </>
            )}

            {isBatch && (
              <>
                <div className="my-3">
                  <FormSelect
                    name="snapshot_collection_id"
                    placeholder="Select snapshot collection"
                    options={snapshotCollectionsOptions}
                    required={true}
                  />
                </div>

                {selectedSnapshotCollection === CREATE_NEW_COLLECTION_KEY && (
                  <FormInput
                    name="snapshot_collection_new"
                    placeholder="New snapshot collection name"
                    className="my-3"
                  />
                )}

                {shouldShowSubcollectionSelect && subcollections.length > 0 && (
                  <div className="my-3">
                    <FormSelect
                      name="subcollection_id"
                      placeholder="Select sub-collection"
                      options={subcollectionOptions}
                    />
                  </div>
                )}

                <label className="my-3 flex items-start gap-2">
                  <Checkbox
                    checked={effectiveUseBatchApi}
                    disabled={lockedExecutionMode !== null}
                    onCheckedChange={checked => setUseBatchApi(checked === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    Use Batch API
                    <span className="block text-xs text-muted-foreground">
                      {lockedExecutionMode
                        ? `This collection already ran in ${lockedExecutionMode === "batch" ? "Batch API" : "async"} mode; the mode is locked for new snapshots.`
                        : "Recommended for very large collections. Each layer of dependent prompts can take up to 24 hours."}
                    </span>
                  </span>
                </label>
              </>
            )}

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
